/**
 * F13.5 commit 3 + F-benefice-fix (2026-05-26) — Coluna 12 da tabela /dossiers.
 * Bénéfice = quanto Euro Storm efetivamente fica (mesmo valor que `eurostormValue`
 * da cascade da coluna 11).
 *
 * Em modelo F8 complementary persistido, o backend já gravou `eurostormPct`
 * (resíduo correto após cliente+tec+interm). Quando esse campo está disponível,
 * usamos direto: `benefice = facturado × eurostormPct / 100`. Caso contrário,
 * derivamos por subtração: `benefice = facturado − cliente − tec − interm`.
 *
 * BUG fix (2026-05-26): antes a fórmula era `facturado − tec − interm`,
 * ignorando o cliente. Em base 'budget' (4 partes), cliente=35 / tec=35 /
 * interm=25 → benefice mostrado como 40% quando o real era 5%. Agora a coluna
 * 12 espelha exatamente a linha "Euro Storm" da cascade.
 *
 * Legacy mode (commissionModel === 'legacy'): usa `technicianCommissionAmount`
 * direto como tecValue; cliente e interm tratados como 0.
 *
 * Cores semânticas:
 *   - lime   : bénéfice > 0  (saudável)
 *   - muted  : bénéfice == 0 (sem operação ou empate)
 *   - danger : bénéfice < 0  (erro contábil)
 */

import { formatMoney } from "../../lib/format-fr.js";

interface Props {
  facturado: number | string | null | undefined;
  /**
   * F8 — % do cliente sobre o orçamento bruto. Só > 0 em base 'budget'.
   * Sem este campo, o cálculo ignora a retenção e superestima o bénéfice.
   */
  clientPct?: number | string | null | undefined;
  techPct?: number | string | null | undefined;
  intermPct?: number | string | null | undefined;
  /**
   * F8 — % do eurostorm efetivo (resíduo). Quando informado, é usado direto
   * (atalho exato); quando ausente, derivamos por subtração.
   */
  eurostormPct?: number | string | null | undefined;
  /**
   * F-cascade-use-amounts (2026-05-27): valor V3 do eurostorm já calculado
   * no banco. Quando presente, é o valor exato — vence sobre o cálculo via
   * pct. Source of truth do calculateCommission, funciona em budget E
   * invoiced sem heurística.
   */
  eurostormAmount?: number | string | null | undefined;
  /** Quando commissionModel === 'legacy', valor já calculado. Sobrescreve techPct. */
  legacyTechAmount?: number | string | null | undefined;
  /**
   * Aceita `string | null | undefined` porque o schema do banco usa `varchar(20)`
   * (não enum), drizzle infere `string`. A função compara `=== 'legacy'` então
   * qualquer valor diferente cai no branch complementary (default seguro).
   */
  commissionModel?: string | null | undefined;
}

function toNum(v: number | string | null | undefined): number {
  if (v == null) return 0;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : 0;
}

export function computeBenefice({
  facturado,
  clientPct,
  techPct,
  intermPct,
  eurostormPct,
  eurostormAmount,
  legacyTechAmount,
  commissionModel,
}: Props): number {
  const fact = toNum(facturado);
  if (fact <= 0) return 0;
  if (commissionModel === "legacy") {
    const tecValue = toNum(legacyTechAmount);
    return fact - tecValue;
  }
  // F-cascade-use-amounts: V3 amount do banco é source of truth. Vence sobre
  // qualquer cálculo via pct — funciona em budget (eurostorm sobre orcamento)
  // E invoiced (eurostorm sobre faturado) sem heurística.
  if (eurostormAmount != null && eurostormAmount !== "") {
    return toNum(eurostormAmount);
  }
  // Complementary fallback (linhas antigas sem amounts persistidos):
  // prefere eurostormPct, senão deriva por subtração.
  if (eurostormPct != null && eurostormPct !== "") {
    return (fact * toNum(eurostormPct)) / 100;
  }
  const clientValue = (fact * toNum(clientPct)) / 100;
  const tecValue = (fact * toNum(techPct)) / 100;
  const intermValue = (fact * toNum(intermPct)) / 100;
  return fact - clientValue - tecValue - intermValue;
}

export function BeneficeCell(props: Props) {
  const value = computeBenefice(props);
  const fact = toNum(props.facturado);
  const empty = fact <= 0;
  const color = empty
    ? "text-text-muted"
    : value > 0
      ? "text-lime"
      : value < 0
        ? "text-danger"
        : "text-text-secondary";
  return (
    <span className={`font-display font-bold tabular-nums text-right block ${color}`}>
      {empty ? "—" : formatMoney(value, { decimals: 2 })}
    </span>
  );
}
